import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { Plus, Trash2, Edit2, ShoppingCart, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

const Satislar = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    musteri_adi: "",
    urun: "",
    miktar: "",
    birim_fiyat: "",
    toplam_tutar: "",
    tarih: new Date(),
    durum: "beklemede"
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/satislar`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (miktar, birimFiyat) => {
    const m = parseFloat(miktar) || 0;
    const b = parseFloat(birimFiyat) || 0;
    return (m * b).toFixed(2);
  };

  const handleMiktarChange = (value) => {
    const toplam = calculateTotal(value, form.birim_fiyat);
    setForm({ ...form, miktar: value, toplam_tutar: toplam });
  };

  const handleBirimFiyatChange = (value) => {
    const toplam = calculateTotal(form.miktar, value);
    setForm({ ...form, birim_fiyat: value, toplam_tutar: toplam });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.musteri_adi || !form.urun || !form.miktar || !form.birim_fiyat) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    try {
      const payload = {
        ...form,
        miktar: parseInt(form.miktar),
        birim_fiyat: parseFloat(form.birim_fiyat),
        toplam_tutar: parseFloat(form.toplam_tutar),
        tarih: form.tarih.toISOString()
      };

      if (editingId) {
        await axios.put(`${API}/satislar/${editingId}`, payload, { withCredentials: true });
        toast.success("Satış güncellendi");
      } else {
        await axios.post(`${API}/satislar`, payload, { withCredentials: true });
        toast.success("Satış eklendi");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error("İşlem sırasında hata oluştu");
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      musteri_adi: item.musteri_adi,
      urun: item.urun,
      miktar: item.miktar.toString(),
      birim_fiyat: item.birim_fiyat.toString(),
      toplam_tutar: item.toplam_tutar.toString(),
      tarih: new Date(item.tarih),
      durum: item.durum
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu satışı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/satislar/${id}`, { withCredentials: true });
      toast.success("Satış silindi");
      fetchItems();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      musteri_adi: "",
      urun: "",
      miktar: "",
      birim_fiyat: "",
      toplam_tutar: "",
      tarih: new Date(),
      durum: "beklemede"
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  const getStatusBadge = (durum) => {
    switch (durum) {
      case "odendi":
        return <span className="badge-paid"><Check size={12} className="mr-1" />Ödendi</span>;
      case "iptal":
        return <span className="badge-overdue"><X size={12} className="mr-1" />İptal</span>;
      default:
        return <span className="badge-pending"><Clock size={12} className="mr-1" />Beklemede</span>;
    }
  };

  const toplamSatis = items.reduce((sum, i) => sum + i.toplam_tutar, 0);
  const odenenSatis = items.filter(i => i.durum === "odendi").reduce((sum, i) => sum + i.toplam_tutar, 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="satislar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
              Satışlar
            </h1>
            <p className="text-[#5E5E62] text-sm mt-1">
              Tüm satışlarınızı takip edin
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="add-satis-btn">
                <Plus size={18} />
                Yeni Satış
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingId ? "Satış Düzenle" : "Yeni Satış Ekle"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Müşteri Adı</label>
                    <input
                      type="text"
                      value={form.musteri_adi}
                      onChange={(e) => setForm({ ...form, musteri_adi: e.target.value })}
                      className="input-default"
                      placeholder="Müşteri adı"
                      data-testid="musteri-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Ürün</label>
                    <input
                      type="text"
                      value={form.urun}
                      onChange={(e) => setForm({ ...form, urun: e.target.value })}
                      className="input-default"
                      placeholder="Ürün adı"
                      data-testid="urun-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Miktar</label>
                    <input
                      type="number"
                      value={form.miktar}
                      onChange={(e) => handleMiktarChange(e.target.value)}
                      className="input-default"
                      placeholder="0"
                      data-testid="miktar-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Birim Fiyat (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.birim_fiyat}
                      onChange={(e) => handleBirimFiyatChange(e.target.value)}
                      className="input-default"
                      placeholder="0.00"
                      data-testid="birim-fiyat-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Toplam (₺)</label>
                    <input
                      type="text"
                      value={form.toplam_tutar}
                      readOnly
                      className="input-default bg-[#F7F7F8]"
                      data-testid="toplam-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tarih</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="input-default text-left" data-testid="tarih-picker">
                          {format(form.tarih, "d MMMM yyyy", { locale: tr })}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.tarih}
                          onSelect={(date) => date && setForm({ ...form, tarih: date })}
                          locale={tr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Durum</label>
                    <Select value={form.durum} onValueChange={(value) => setForm({ ...form, durum: value })}>
                      <SelectTrigger data-testid="durum-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beklemede">Beklemede</SelectItem>
                        <SelectItem value="odendi">Ödendi</SelectItem>
                        <SelectItem value="iptal">İptal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <button type="submit" className="btn-primary w-full" data-testid="submit-satis-btn">
                  {editingId ? "Güncelle" : "Kaydet"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart size={18} className="text-[#002FA7]" />
              <span className="text-sm text-[#5E5E62]">Toplam Satış</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#002FA7]">{formatCurrency(toplamSatis)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Check size={18} className="text-[#00C566]" />
              <span className="text-sm text-[#5E5E62]">Ödenen</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#00C566]">{formatCurrency(odenenSatis)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-[#FF9500]" />
              <span className="text-sm text-[#5E5E62]">Bekleyen</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#FF9500]">{formatCurrency(toplamSatis - odenenSatis)}</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="satislar-table">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="table-header">Müşteri</th>
                  <th className="table-header">Ürün</th>
                  <th className="table-header">Miktar</th>
                  <th className="table-header">Birim Fiyat</th>
                  <th className="table-header">Toplam</th>
                  <th className="table-header">Tarih</th>
                  <th className="table-header">Durum</th>
                  <th className="table-header w-24"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-[#5E5E62]">
                      Henüz satış bulunmuyor
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="table-row">
                      <td className="table-cell font-medium">{item.musteri_adi}</td>
                      <td className="table-cell">{item.urun}</td>
                      <td className="table-cell font-mono">{item.miktar}</td>
                      <td className="table-cell font-mono">{formatCurrency(item.birim_fiyat)}</td>
                      <td className="table-cell font-mono font-medium text-[#002FA7]">
                        {formatCurrency(item.toplam_tutar)}
                      </td>
                      <td className="table-cell font-mono text-[#5E5E62]">
                        {new Date(item.tarih).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="table-cell">{getStatusBadge(item.durum)}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-[#5E5E62] hover:bg-[#F7F7F8] rounded-sm transition-colors"
                            data-testid={`edit-${item.id}`}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-[#FF3B30] hover:bg-[#FFEBEE] rounded-sm transition-colors"
                            data-testid={`delete-${item.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Satislar;
